import React, { useState, useEffect } from 'react';

import { Modal, Button, Alert } from 'react-bootstrap';
import styles from './ModifySubscriptionModal.module.css';

import { useModifySubscription } from '../hooks/stripeHooks';

const ModifySubscriptionModal = ({ show, handleClose, currentPlanId }) => {
    const [selectedPlan, setSelectedPlan] = useState('');
    const [selectedPlanPriceId, setSelectedPlanPriceId] = useState('');
    const [showPlanChangeMessage, setShowPlanChangeMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const { modifySubscription, loading: modifySubscriptionLoading, error: modifySubscriptionError } = useModifySubscription();

    const subscriptionPlans = [
        {
            id: process.env.REACT_APP_MONTHLY_SUBSCRIPTION_PRICE_ID,
            name: 'Monthly',
            price: 5,
            period: 'month',
            description: 'Billed monthly'
        },
        {
            id: process.env.REACT_APP_ANNUAL_SUBSCRIPTION_PRICE_ID, // Updated name
            name: 'Annual', // Updated to be consistent
            price: 48,
            period: 'year',
            description: 'Billed annually',
            savings: 'Save 20%'
        }
    ];


    // Initialize selected plan when component mounts or currentPlanId changes
    useEffect(() => {
        if (currentPlanId) {
            setSelectedPlan(currentPlanId);
            setSelectedPlanPriceId(currentPlanId);
        }
    }, [currentPlanId]);

    const handlePlanSelection = (planId) => {

        const selectedPlan = subscriptionPlans.find(p => p.id === planId);
        if (!selectedPlan) {
            console.warn('No matching plan found for ID:', planId);
            return;
        }

        // Set both the plan and its price ID
        setSelectedPlan(planId);
        setSelectedPlanPriceId(selectedPlan.id); // This is the price ID we'll send to Stripe


        // Show message if selecting a different plan than current
        if (planId !== currentPlanId) {
            setShowPlanChangeMessage(
                `You have selected a new plan. Your current subscription will remain active until September 21, then you will be charged $${selectedPlan.price}/${selectedPlan.period}. Continue?`
            );
        } else {
            // Clear message if selecting current plan
            setShowPlanChangeMessage('');
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // If no change in plan, just close the modal
        if (selectedPlan === currentPlanId) {
            handleClose();
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Call API to modify subscription with the price ID
            console.log('Modifying subscription to price ID:', selectedPlanPriceId);
            await modifySubscription(selectedPlanPriceId);
            handleClose();
            window.location.reload(); // Reload to show updated subscription
        } catch (err) {
            setError('Failed to modify subscription. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Choose Your Plan</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                

                <div className={styles.currentPlanContainer} style={{marginBottom: '20px'}}>
                    You are currently on the {subscriptionPlans.find(p => p.id === currentPlanId)?.name || 'Monthly'} plan. 
                    It is set to renew on <strong>Saturday, September 21, 2025</strong>.
                </div>
                
                <div className={styles.planOptionsContainer}>
                    {subscriptionPlans.map(plan => {
                        return (
                            <div
                                key={plan.id}
                                className={selectedPlan === plan.id ? styles.planOptionSelected : styles.planOption}
                                onClick={() => {
                                    handlePlanSelection(plan.id);
                                }}
                            >
                                <div className={styles.planName}>{plan.name}</div>
                                <div className={styles.planPrice}>
                                    ${plan.price}
                                    <span className={styles.planPeriod}>/{plan.period}</span>
                                </div>
                                <div className={styles.planPeriod}>{plan.description}</div>
                                {plan.savings && (
                                    <div className={styles.savings}>{plan.savings}</div>
                                )}
                            </div>
                        );
                    })}
                </div>
                {showPlanChangeMessage && (
                    <Alert variant="warning" onClose={() => setShowPlanChangeMessage('')} dismissible>
                        {showPlanChangeMessage}
                    </Alert>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleSubmit}
                    disabled={!selectedPlan || selectedPlan === currentPlanId || loading}
                >
                    {(() => {
                        return loading ? 'Updating...' : 'Update Subscription';
                    })()}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ModifySubscriptionModal;
